import React, { useReducer, ReactNode } from "react";
import { useContextSelector, createContext } from "use-context-selector";

import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";

interface State {
	byBank: { [key: string]: SocketMessageInterface };
	byLift: { [key: string]: SocketMessageInterface };
	last?: SocketMessageInterface;
}

type Action = { type: "UPDATE_MESSAGE"; payload: SocketMessageInterface } | { type: "CLEAR_MESSAGES" };

const BankMessagesContext = createContext<State | undefined>(undefined);
const BankMessagesDispatch = createContext<React.Dispatch<Action> | undefined>(undefined);

function reducer(state: State, action: Action): State {
	switch (action.type) {
		case "UPDATE_MESSAGE": {
			const msg = action.payload;
			return {
				byBank: { ...state.byBank, [msg.bank_id]: msg },
				byLift: { ...state.byLift, [msg.lift_id]: msg },
				last: msg,
			};
		}
		case "CLEAR_MESSAGES": {
			return { byBank: {}, byLift: {}, last: undefined };
		}
		default:
			return state;
	}
}

export const BankMessagesProvider = ({ children }: { children: ReactNode }) => {
	const [state, dispatch] = useReducer(reducer, { byBank: {}, byLift: {}, last: undefined });

	return (
		<BankMessagesContext.Provider value={state}>
			<BankMessagesDispatch.Provider value={dispatch}>{children}</BankMessagesDispatch.Provider>
		</BankMessagesContext.Provider>
	);
};

export function useLastBankMessage(bankId: string): SocketMessageInterface | undefined {
	return useContextSelector(BankMessagesContext, (ctx) => ctx?.byBank[bankId]);
}

export function useLastLiftMessage(liftId: string): SocketMessageInterface | undefined {
	return useContextSelector(BankMessagesContext, (ctx) => ctx?.byLift[liftId]);
}

export function useLastSocketMessage(): SocketMessageInterface | undefined {
	return useContextSelector(BankMessagesContext, (ctx) => ctx?.last);
}

export function useBankDispatch() {
	return useContextSelector(BankMessagesDispatch, (dispatch) => {
		if (!dispatch) throw new Error("useBankDispatch must be used within BankMessagesProvider");
		return dispatch;
	});
}

export function useClearBankMessages() {
	const dispatch = useBankDispatch();
	return () => dispatch({ type: "CLEAR_MESSAGES" });
}
